import { FormEvent, useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'
import type { User, UserRole } from 'chat-shared'
import { api, ApiError } from '../../api/client.js'
import { useAuth } from '../../auth/AuthContext.js'
import {
  AdminCard,
  AdminPageHeader,
  AdminTableEmpty,
  adminTableSx,
} from '../../components/AdminUi.js'

const userErrorMessage = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.message === 'email_taken') return 'Diese E-Mail-Adresse wird bereits verwendet.'
    if (err.message === 'cannot_modify_own_admin_status') {
      return 'Du kannst deinen eigenen Admin-/Aktiv-Status nicht ändern.'
    }
    if (err.message === 'invalid_body') return 'Bitte alle Felder korrekt ausfüllen.'
    if (err.message === 'mail_not_configured') {
      return 'E-Mail-Versand ist nicht konfiguriert — bitte ein Passwort vergeben.'
    }
  }
  return 'Speichern fehlgeschlagen'
}

/** Adminbereich → Nutzer: Liste, Anlegen (mit Willkommens-Mail) und Bearbeiten. */
export const AdminUsersSection = () => {
  const { user: currentUser } = useAuth()
  const [notice, setNotice] = useState<
    { severity: 'success' | 'error'; message: string } | undefined
  >(undefined)
  const [users, setUsers] = useState<Array<User>>([])
  // Deaktivierte Nutzer sind standardmäßig ausgeblendet und nur über den
  // Switch im Listenkopf sichtbar.
  const [showDisabled, setShowDisabled] = useState(false)

  const [editUserTarget, setEditUserTarget] = useState<User | undefined>(undefined)
  const [editDisplayName, setEditDisplayName] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [editPassword, setEditPassword] = useState('')
  const [editRole, setEditRole] = useState<UserRole>('user')
  const [editIsDisabled, setEditIsDisabled] = useState(false)
  const [userError, setUserError] = useState<string | undefined>(undefined)

  const [creatingUser, setCreatingUser] = useState(false)
  const [newUserName, setNewUserName] = useState('')
  const [newUserEmail, setNewUserEmail] = useState('')
  const [newUserPassword, setNewUserPassword] = useState('')
  const [newUserRole, setNewUserRole] = useState<UserRole>('user')
  const [createUserError, setCreateUserError] = useState<string | undefined>(undefined)

  const reloadUsers = () => {
    api.listAdminUsers().then(setUsers).catch(() => setUsers([]))
  }
  useEffect(reloadUsers, [])

  const visibleUsers = users.filter((user) => showDisabled || !user.isDisabled)

  const openEditUser = (user: User) => {
    setEditUserTarget(user)
    setEditDisplayName(user.displayName)
    setEditEmail(user.email)
    setEditPassword('')
    setEditRole(user.role)
    setEditIsDisabled(user.isDisabled)
    setUserError(undefined)
  }

  const handleSaveUser = async () => {
    if (!editUserTarget) return
    setUserError(undefined)
    try {
      await api.updateAdminUser(editUserTarget.id, {
        displayName: editDisplayName,
        email: editEmail,
        password: editPassword.length > 0 ? editPassword : undefined,
        role: editRole,
        isDisabled: editIsDisabled,
      })
      setUsers((prev) =>
        prev.map((u) =>
          u.id === editUserTarget.id
            ? {
                ...u,
                displayName: editDisplayName,
                email: editEmail,
                role: editRole,
                isDisabled: editIsDisabled,
              }
            : u,
        ),
      )
      setEditUserTarget(undefined)
    } catch (err) {
      setUserError(userErrorMessage(err))
    }
  }

  const handleCreateUser = async (event: FormEvent) => {
    event.preventDefault()
    setCreateUserError(undefined)
    try {
      const created = await api.createAdminUser({
        displayName: newUserName,
        email: newUserEmail,
        // Leer = Server verschickt eine Willkommens-Mail mit Reset-Link.
        password: newUserPassword.length > 0 ? newUserPassword : undefined,
        role: newUserRole,
      })
      setNotice(
        created.welcomeEmailSent
          ? {
              severity: 'success',
              message: `Nutzer angelegt — E-Mail zum Setzen des Passworts an ${created.email} versendet.`,
            }
          : { severity: 'success', message: 'Nutzer angelegt. Es wurde keine E-Mail versendet.' },
      )
      setNewUserName('')
      setNewUserEmail('')
      setNewUserPassword('')
      setNewUserRole('user')
      setCreatingUser(false)
      reloadUsers()
    } catch (err) {
      setCreateUserError(userErrorMessage(err))
    }
  }

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {notice && (
        <Alert severity={notice.severity} onClose={() => setNotice(undefined)}>
          {notice.message}
        </Alert>
      )}

      <AdminPageHeader
        title="Nutzer"
        description="Alle Benutzerkonten dieser Installation — anlegen, bearbeiten und deaktivieren."
        actions={
          <Button
            variant="contained"
            onClick={() => {
              setCreateUserError(undefined)
              setCreatingUser(true)
            }}
          >
            Neuer Nutzer
          </Button>
        }
      />

      <AdminCard
        title={`Alle Nutzer (${visibleUsers.length})`}
        actions={
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showDisabled}
                onChange={(e) => setShowDisabled(e.target.checked)}
              />
            }
            label="Deaktivierte anzeigen"
            slotProps={{ typography: { variant: 'body2', color: 'text.secondary' } }}
          />
        }
      >
        <Table size="small" sx={adminTableSx}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>E-Mail</TableCell>
              <TableCell>Angelegt</TableCell>
              <TableCell>Zuletzt aktiv</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleUsers.map((user) => (
              <TableRow key={user.id} hover onClick={() => openEditUser(user)} sx={{ cursor: 'pointer' }}>
                <TableCell>{user.displayName}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{DateTime.fromISO(user.createdAt).toFormat('dd.MM.yyyy')}</TableCell>
                <TableCell>
                  {user.lastActiveAt ? DateTime.fromISO(user.lastActiveAt).toFormat('dd.MM.yyyy HH:mm') : '–'}
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    {user.role === 'system-admin' && <Chip size="small" color="primary" label="Admin" />}
                    <Chip
                      size="small"
                      color={user.isDisabled ? 'default' : 'success'}
                      label={user.isDisabled ? 'Deaktiviert' : 'Aktiv'}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {visibleUsers.length === 0 && (
              <AdminTableEmpty colSpan={5}>
                {showDisabled ? 'Keine Nutzer vorhanden.' : 'Keine aktiven Nutzer vorhanden.'}
              </AdminTableEmpty>
            )}
          </TableBody>
        </Table>
      </AdminCard>

      <Dialog open={Boolean(editUserTarget)} onClose={() => setEditUserTarget(undefined)} maxWidth="xs" fullWidth>
        <DialogTitle>Nutzer bearbeiten</DialogTitle>
        <DialogContent>
          {userError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {userError}
            </Alert>
          )}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Name"
              size="small"
              value={editDisplayName}
              onChange={(e) => setEditDisplayName(e.target.value)}
            />
            <TextField
              label="E-Mail"
              type="email"
              size="small"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
            />
            <TextField
              label="Neues Passwort"
              type="password"
              size="small"
              value={editPassword}
              onChange={(e) => setEditPassword(e.target.value)}
              autoComplete="new-password"
              helperText="Leer lassen, um das Passwort nicht zu ändern (min. 8 Zeichen)."
            />
            <TextField
              label="Rolle"
              select
              size="small"
              value={editRole}
              disabled={editUserTarget?.id === currentUser?.id}
              onChange={(e) => setEditRole(e.target.value === 'system-admin' ? 'system-admin' : 'user')}
              helperText="System-Admins dürfen alles — inklusive Nutzer- und Org-Verwaltung."
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="system-admin">System-Admin</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editIsDisabled}
                  disabled={editUserTarget?.id === currentUser?.id}
                  onChange={(e) => setEditIsDisabled(e.target.checked)}
                />
              }
              label="Deaktiviert"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUserTarget(undefined)}>Abbrechen</Button>
          <Button variant="contained" onClick={() => void handleSaveUser()}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={creatingUser} onClose={() => setCreatingUser(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Neuer Nutzer</DialogTitle>
        <DialogContent>
          {createUserError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createUserError}
            </Alert>
          )}
          <Box component="form" onSubmit={handleCreateUser} display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Name"
              size="small"
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              required
              autoFocus
            />
            <TextField
              label="E-Mail"
              type="email"
              size="small"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              required
            />
            <TextField
              label="Passwort (optional)"
              type="password"
              size="small"
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              autoComplete="new-password"
              slotProps={{ htmlInput: { minLength: 8 } }}
              helperText="Leer lassen: Der Nutzer erhält eine E-Mail mit einem Link zum Setzen des Passworts."
            />
            <TextField
              label="Rolle"
              select
              size="small"
              value={newUserRole}
              onChange={(e) => setNewUserRole(e.target.value === 'system-admin' ? 'system-admin' : 'user')}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="system-admin">System-Admin</MenuItem>
            </TextField>
            <Button type="submit" variant="contained">
              Anlegen
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}
